<script setup lang="ts">
import { computed, ref, useClock, useDayjs } from '#imports'
import type { BlogPostListItem } from '@core/types/blog'

export interface NimbusBlogAppProps {
    title: string
    subtitle: string
    searchFieldPlaceholder: string
    errorOptions: {
        noSearchResultText: string
        noBlogsText: string
    }
    posts: BlogPostListItem[]
}

const props = defineProps<NimbusBlogAppProps>()

const searchQuery = ref('')

const dayjs = useDayjs()
const { clock } = useClock()

const now = dayjs(clock.value)

const postsWithRelativeDate = computed(() =>
    props.posts.map(post => ({
        ...post,
        relativeDate: dayjs(post.publish_at).from(now),
    })))

const listOfBlogs = computed(() =>
    postsWithRelativeDate.value.filter(post => post.title.toLowerCase().includes(searchQuery.value.toLowerCase())))
</script>

<template>
    <section class="blog-section">
        <div class="blog-header-wrapper">
            <div class="blog-section__header">
                <h1 class="blog-section__title">
                    {{ title }}
                </h1>

                <div class="blog-section__subtitle">
                    {{ subtitle }}
                </div>
            </div>
        </div>

        <UIInput
            v-model="searchQuery"
            name="search"
            type="input"
        >
            {{ searchFieldPlaceholder }}
        </UIInput>

        <div v-if="listOfBlogs?.length" class="blog-section__list">
            <NuxtLink
                v-for="blog in listOfBlogs"
                :key="blog.slug"
                :to="{ name: 'blog-slug', params: { slug: blog.slug } }"
                class="blog-section__post"
            >
                <NimbusBlogPost
                    :title="blog.title"
                    :subtitle="blog.subtitle"
                    :label="blog.label"
                    :media-url="blog.hero_url"
                    :relative-date="blog.relativeDate"
                    :published-at="blog.publish_at"
                />
            </NuxtLink>
        </div>

        <div v-else-if="searchQuery?.length">
            <div>
                {{ errorOptions.noSearchResultText }}
            </div>
        </div>

        <div v-else>
            {{ errorOptions.noBlogsText }}
        </div>
    </section>
</template>

<style lang="scss" scoped>
.blog-section {
    display: flex;
    flex-direction: column;
    gap: 20px;

    &__title {
        font-family: $brand-font-family;
        font-weight: $text-bold;
    }

    &__list {
        display: flex;
        flex-direction: column;
        gap: 10px;
    }

    &__post {
        text-decoration: none;
    }
}
</style>
